import { createI18n } from 'vue-i18n'
import { useStorage } from '@vueuse/core'
import type { LangCode, I18nLocale, LangCodeMap, LanguageInfo } from '@/types/config'

type LocaleMessages = Record<string, unknown>

type AppI18nGlobal = {
  locale: {
    value: I18nLocale
  }
  availableLocales: readonly string[]
  setLocaleMessage: (locale: I18nLocale, messages: LocaleMessages) => void
}

const localeLoaders = import.meta.glob('./locales/*.json', {
  import: 'default'
})

// 语言代码映射配置 (使用BCP 47标准)
const LANG_CODE_MAP: LangCodeMap = {
  'zh-CN': 'zh-CN',
  'en-US': 'en-US',
  'vi-VN': 'vi-VN',
  'hi-IN': 'hi-IN',
  'ta-IN': 'ta-IN',
  'ms-MY': 'ms-MY',
  'pt-BR': 'pt-BR'
} as const

// 支持的语言信息
export const SUPPORTED_LANGUAGES: readonly LanguageInfo[] = [
  { code: 'zh-CN', i18nCode: 'zh-CN', name: '中文 (简体)', nativeName: '中文 (简体)', flag: '🇨🇳' },
  {
    code: 'en-US',
    i18nCode: 'en-US',
    name: 'English (US)',
    nativeName: 'English (US)',
    flag: '🇺🇸'
  },
  { code: 'vi-VN', i18nCode: 'vi-VN', name: 'Vietnamese', nativeName: 'Tiếng Việt', flag: '🇻🇳' },
  { code: 'hi-IN', i18nCode: 'hi-IN', name: 'Hindi', nativeName: 'हिन्दी', flag: '🇮🇳' },
  { code: 'ta-IN', i18nCode: 'ta-IN', name: 'Tamil', nativeName: 'தமிழ்', flag: '��🇳' },
  { code: 'ms-MY', i18nCode: 'ms-MY', name: 'Malay', nativeName: 'Bahasa Melayu', flag: '🇲🇾' },
  {
    code: 'pt-BR',
    i18nCode: 'pt-BR',
    name: 'Portuguese (Brazil)',
    nativeName: 'Português (Brasil)',
    flag: '🇧🇷'
  }
] as const

// 当前语言设置 (用户偏好，持久化存储)
const currentLang = useStorage<LangCode>(
  'CURRENT_LANG',
  (import.meta.env.VITE_DEFAULT_LANG as LangCode) || 'zh-CN'
)

// 语言包在启动和切换语言时按需加载
export const i18n = createI18n({
  locale: getI18nLocale(currentLang.value),
  fallbackLocale: 'zh-CN',
  messages: {},
  legacy: false
})

const globalComposer = i18n.global as unknown as AppI18nGlobal

/**
 * 获取 i18n 语言代码
 */
export function getI18nLocale(langCode: LangCode): I18nLocale {
  return LANG_CODE_MAP[langCode] || 'zh-CN'
}

async function loadLocaleMessages(locale: I18nLocale): Promise<void> {
  if (globalComposer.availableLocales.includes(locale)) return

  const loader = localeLoaders[`./locales/${locale}.json`]
  if (!loader) throw new Error(`Missing locale messages: ${locale}`)

  const messages = (await loader()) as LocaleMessages
  globalComposer.setLocaleMessage(locale, messages)
}

export async function setupI18n(): Promise<void> {
  const locale = getI18nLocale(currentLang.value)
  const initialLocales = [...new Set<I18nLocale>([locale, 'zh-CN'])]

  await Promise.all(initialLocales.map(loadLocaleMessages))
  globalComposer.locale.value = locale
}

/**
 * 切换语言 (纯本地配置)
 */
export async function switchLanguage(langCode: LangCode): Promise<void> {
  if (['zh-CN', 'en-US', 'vi-VN', 'hi-IN', 'ta-IN', 'ms-MY', 'pt-BR'].includes(langCode)) {
    const i18nLocale = getI18nLocale(langCode)
    await loadLocaleMessages(i18nLocale)

    currentLang.value = langCode
    globalComposer.locale.value = i18nLocale

    console.log('🔄 语言已切换到:', { langCode, i18nLocale })
  } else {
    console.warn('⚠️ 不支持的语言代码:', langCode)
  }
}

/**
 * 获取当前语言代码
 */
export function getCurrentLanguage(): LangCode {
  return currentLang.value
}

/**
 * 获取支持的语言列表
 */
export function getSupportedLocales(): readonly LanguageInfo[] {
  return SUPPORTED_LANGUAGES
}

export default i18n
